import { RequestError } from '@/services/config';

export interface ProviderFieldError {
    /** Keyed as the form keys its inline message, e.g. `pens.101.external_unit_ids`. */
    path: string;
    code: string;
}

export type ProviderConfigError =
    | { kind: 'invalid'; errors: ProviderFieldError[] }
    | { kind: 'stale'; rowIds: number[] }
    | { kind: 'alreadyConnected'; cageIds: number[] }
    | { kind: 'duplicatePen'; cageIds: number[] }
    | { kind: 'rowHasData'; cageIds: number[] }
    | { kind: 'testRequired' }
    /** The save committed, but the server could not read the fresh config back. */
    | { kind: 'savedUnread' }
    | { kind: 'encryptionUnavailable' }
    | { kind: 'rateLimited' }
    /** The request is past the API's JSON body limit. */
    | { kind: 'tooLarge' }
    | { kind: 'forbidden' }
    | { kind: 'unauthenticated' }
    | { kind: 'notFound' }
    /** A 400 from request validation (class-validator), which carries no code. */
    | { kind: 'badRequest'; messages: string[] }
    | { kind: 'other'; status: number | null };

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function numbers(value: unknown): number[] {
    if (!Array.isArray(value)) return [];
    return value.filter((entry): entry is number => typeof entry === 'number');
}

function fieldErrors(value: unknown): ProviderFieldError[] {
    if (!Array.isArray(value)) return [];
    return value.flatMap((entry): ProviderFieldError[] =>
        isRecord(entry) && typeof entry.path === 'string' && typeof entry.code === 'string'
            ? [{ path: entry.path, code: entry.code }]
            : []
    );
}

function messages(value: unknown): string[] {
    if (typeof value === 'string') return [value];
    if (!Array.isArray(value)) return [];
    return value.filter((entry): entry is string => typeof entry === 'string');
}

function fromCode(code: string, body: Record<string, unknown>): ProviderConfigError | null {
    switch (code) {
        case 'PROVIDER_CONFIG_INVALID':
            return { kind: 'invalid', errors: fieldErrors(body.errors) };
        case 'PROVIDER_CONFIG_STALE':
            return { kind: 'stale', rowIds: numbers(body.rowIds) };
        case 'PROVIDER_CONFIG_ALREADY_CONNECTED':
            return { kind: 'alreadyConnected', cageIds: numbers(body.cageIds) };
        case 'PROVIDER_CONFIG_DUPLICATE_PEN':
            return { kind: 'duplicatePen', cageIds: numbers(body.cageIds) };
        case 'ROW_HAS_DATA':
            return { kind: 'rowHasData', cageIds: numbers(body.cageIds) };
        case 'TEST_REQUIRED':
            return { kind: 'testRequired' };
        case 'PROVIDER_CONFIG_SAVED_UNREAD':
            return { kind: 'savedUnread' };
        case 'TEST_OVERRIDE_NOT_ALLOWED':
            return { kind: 'forbidden' };
        case 'ENCRYPTION_KEY_MISSING':
            return { kind: 'encryptionUnavailable' };
        case 'RATE_LIMITED':
            return { kind: 'rateLimited' };
        case 'PEN_NOT_FOUND':
        case 'PROVIDER_NOT_FOUND':
            return { kind: 'notFound' };
    }
    // A provider's failure is an answer with a status, never an error (catalog and test).
    return null;
}

function fromStatus(status: number | null, body: unknown): ProviderConfigError {
    switch (status) {
        // withSerializableRetry answers a bare 409 once its last retry also conflicted.
        case 409:
            return { kind: 'stale', rowIds: [] };
        case 400:
            return { kind: 'badRequest', messages: isRecord(body) ? messages(body.message) : [] };
        case 401:
            return { kind: 'unauthenticated' };
        case 403:
            return { kind: 'forbidden' };
        case 404:
            return { kind: 'notFound' };
        case 413:
            return { kind: 'tooLarge' };
        case 429:
            return { kind: 'rateLimited' };
        default:
            return { kind: 'other', status };
    }
}

/** Reads the body `code` first and falls back to the HTTP status. */
export function parseProviderConfigError(error: unknown): ProviderConfigError {
    if (!RequestError.isRequestError(error)) return { kind: 'other', status: null };
    const status = error.status ?? error.response?.status ?? null;
    const body: unknown = error.response?.data;
    if (isRecord(body) && typeof body.code === 'string') {
        const parsed = fromCode(body.code, body);
        if (parsed !== null) return parsed;
    }
    return fromStatus(status, body);
}

/** For hooks: no error yet reads as null rather than as a parsed `other`. */
export function parsedErrorOrNull(error: unknown): ProviderConfigError | null {
    return error == null ? null : parseProviderConfigError(error);
}
